/**
 * Protected Route Component
 * Restricts access based on authentication and role
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types/User.types';

interface ProtectedRouteProps {
  children: React.ReactElement;
  allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="textSecondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Enforce role-based access: redirect to the user's home route if their role
  // is not in the allowedRoles list for this route.
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role as UserRole)) {
    const roleHomeMap: Partial<Record<UserRole, string>> = {
      [UserRole.EMPLOYEE]: '/my-work',
      [UserRole.SUPERVISOR]: '/upload',
      [UserRole.MANAGER]: '/allocation/approvals',
      [UserRole.OPERATIONS_MANAGER]: '/dashboard',
      [UserRole.DIRECTOR]: '/dashboard',
      [UserRole.ADMIN]: '/dashboard',
      [UserRole.OPEX]: '/reports',
    };
    const home = roleHomeMap[user.role as UserRole] ?? '/dashboard';
    return <Navigate to={home} state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
